import React from 'react';
import {Body, Button, Icon} from '@lightricks/react-design-system';
import numberAbbreviation from '@/utils/numberAbbreviation';
import translate from '@/utils/translate';
import LEGACY_COLORS from '@/config/legacyColors';
import InfoTooltip from '@/components/info-tooltip/InfoTooltip';
import styles from './ShortlistSummaryBar.module.scss';

// Matches the V1 brand-header and the V2 campaign cost summary.
const COST_FORMAT = '$0,0.00';

interface ShortlistSummaryBarProps {
  // Count of currently-rendered shortlisted rows, which can differ from
  // `totalCostInDollars` (campaign-wide) when a filter is active.
  count: number;
  // Campaign-wide shortlisted total. `null` hides the cost entirely; a real
  // 0 still renders as "$0.00".
  totalCostInDollars: number | null;
  onClearShortlist?: () => void;
}

function ShortlistSummaryBar({
  count,
  totalCostInDollars,
  onClearShortlist,
}: ShortlistSummaryBarProps) {
  const tooltipText = translate(
    'The total dollar amount of shortlisted creator bids for this campaign.'
  );

  return (
    <div className={styles.container} data-testid="shortlist-summary-bar">
      <div className={styles.iconWrap}>
        <Icon
          size="small"
          appearance="neutral"
          name="Navigation-Heart-Fill"
          color={LEGACY_COLORS.PURPLE_MEDIUM}
        />
      </div>
      <div className={styles.label}>
        <Body
          size="md"
          color={LEGACY_COLORS.PURPLE_DARK}
          sx={{fontWeight: 600}}
        >
          {translate('Showing {count} shortlisted creators', {count})}
        </Body>
        {totalCostInDollars !== null ? (
          <>
            <span className={styles.separator} aria-hidden />
            <div className={styles.cost}>
              <Body size="md" color={LEGACY_COLORS.PURPLE_DARK}>
                {translate('{amount} shortlisted cost, USD', {
                  amount: numberAbbreviation(totalCostInDollars, COST_FORMAT),
                })}
              </Body>
              <span className={styles.infoIconWrap}>
                <InfoTooltip
                  title={tooltipText}
                  placement="top"
                  iconSize="small"
                  iconColor={LEGACY_COLORS.PURPLE_MEDIUM}
                />
              </span>
            </div>
          </>
        ) : null}
      </div>
      {onClearShortlist ? (
        <Button
          size="small"
          mode="plain"
          appearance="neutral"
          onClick={onClearShortlist}
          className={styles.clearButton}
        >
          {translate('Clear shortlist')}
        </Button>
      ) : null}
    </div>
  );
}

export default ShortlistSummaryBar;
